import { Asset } from "contentful";
import {
  ActivationState,
  GuidedSignUpAnswers,
} from "components/Activation/activationTypes";
import { ReadingSkillJsonItem, Step } from "types/contentful";
import { StorageGetItem } from "utils/hooks/useWindowStorage";
import { ReadingLevelImageMap } from "utils/readingLevelImageUtils";
import { getThemePreferencesStep } from "components/Step/StepThemePreferences";
import { ThemePreferenceKey } from "components/Step/stepTypes";

export const STORED_ACTIVATION_ITEM_KEY = "activationKey";

export const getActivationStorageKey = ({
  contentfulQuizModelSlug,
  getItemFromStorage,
  giftCode,
}: {
  contentfulQuizModelSlug?: string;
  getItemFromStorage?: StorageGetItem;
  giftCode?: string;
}) => {
  if (contentfulQuizModelSlug && giftCode) {
    return `${contentfulQuizModelSlug}-${giftCode}`;
  } else {
    const storedKey = getItemFromStorage?.(
      STORED_ACTIVATION_ITEM_KEY
    ) as string;
    return storedKey || null;
  }
};

export function getActivationStorage({
  contentfulQuizModel,
  getItemFromStorage,
  giftCode,
}: {
  contentfulQuizModel?: any;
  getItemFromStorage?: StorageGetItem;
  giftCode?: string;
}) {
  const storageKey = getActivationStorageKey({
    contentfulQuizModelSlug: contentfulQuizModel?.slug,
    getItemFromStorage,
    giftCode,
  });

  if (!storageKey) {
    return null;
  }

  const storage = getItemFromStorage?.(storageKey) as ActivationState;
  return storage;
}

export function deriveStepSavedValues({
  contentfulQuizModel,
  formKeys,
  getItemFromStorage,
  guidedSignUpAnswers,
  stateKey,
  giftCode,
}: {
  contentfulQuizModel?: any;
  formKeys: string[];
  getItemFromStorage?: StorageGetItem;
  guidedSignUpAnswers?: GuidedSignUpAnswers;
  stateKey: keyof ActivationState;
  giftCode?: string;
}) {
  const storage = getActivationStorage({
    contentfulQuizModel,
    getItemFromStorage,
    giftCode,
  });

  if (stateKey === "themePreferenceValues") {
    return getThemePreferenceStepSavedValues({
      contentfulQuizModel,
      guidedSignUpAnswers,
      storage,
    });
  }

  const savedValues = formKeys.reduce((acc, key) => {
    const sessionValue = guidedSignUpAnswers?.[key]?.[0]?.value;
    const storageValue = storage?.[stateKey]?.[key];
    const value = (sessionValue && String(sessionValue)) || storageValue;

    return value ? { ...acc, [key]: value } : acc;
  }, {});

  return savedValues;
}

export function getThemePreferenceStepSavedValues({
  contentfulQuizModel,
  guidedSignUpAnswers,
  storage,
}: {
  contentfulQuizModel?: any;
  guidedSignUpAnswers?: GuidedSignUpAnswers;
  storage?: ActivationState | null;
}) {
  const step = getThemePreferencesStep(contentfulQuizModel) as Step;
  const stepAnswers = step?.questions?.[0]?.answers;

  const themeKey = ThemePreferenceKey.THEME_PREFERENCES;
  const sessionAnswers = guidedSignUpAnswers?.[themeKey];
  const storageAnswers = storage?.themePreferenceValues?.[themeKey];

  const savedAnswers = stepAnswers?.map((answer) => {
    const slug = answer.slug;

    const sessionValue = sessionAnswers?.find((a) => a.slug === slug)?.value;
    const storageValue = storageAnswers?.find((a) => a.slug === slug)?.value;
    const value = storageValue || sessionValue || 0;

    return { ...answer, value };
  });

  return {
    [ThemePreferenceKey.KIND]: "reader-theme-preferences",
    [ThemePreferenceKey.THEME_PREFERENCES]: savedAnswers || [],
  };
}

export function getReadingLevel(
  contentfulQuizModel: any,
  readingLevelName: string
) {
  const readingSkills: ReadingSkillJsonItem[] =
    contentfulQuizModel?.readingSkills?.json || [];

  const readingLevel = readingSkills?.find(
    (skill) =>
      skill?.name?.toLowerCase() === readingLevelName?.toLocaleLowerCase()
  );

  if (!readingLevel) {
    console.warn("setReadingLevel: `readingLevel` could not be determined");
    return;
  }

  return readingLevel;
}

export function getReadingLevelImage(
  readingLevelImagesByName?: ReadingLevelImageMap,
  readingLevelName?: string
): Asset["fields"] | undefined {
  if (!readingLevelImagesByName || !readingLevelName) {
    return;
  }

  const readingLevelImage =
    readingLevelImagesByName[readingLevelName.toLowerCase()]?.fields;

  return readingLevelImage;
}
